import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from "react";
import AnyaAPI from "restAPI/AnyaAPI";
import { ApiRequestInfo, PreFetchedApiData } from "types/anya";
import { generateApiKey } from "utils/apiKeyGenerator";

interface WidgetApiInterceptorContextType {
  intercept: <T>(
    promise: Promise<T>,
    requestInfo?: ApiRequestInfo,
  ) => Promise<T>;
}

const WidgetApiInterceptorContext =
  createContext<WidgetApiInterceptorContextType>({
    intercept: (promise) => promise,
  });

interface WidgetApiInterceptorProviderProps {
  children: ReactNode;
  preFetchedData?: PreFetchedApiData;
  conversationId: string;
  messageId: string;
  widgetId: string;
}

export function WidgetApiInterceptorProvider({
  children,
  preFetchedData,
  conversationId,
  messageId,
  widgetId,
}: WidgetApiInterceptorProviderProps) {
  const intercept = useCallback(
    async <T,>(
      promise: Promise<T>,
      requestInfo?: ApiRequestInfo,
    ): Promise<T> => {
      // If no requestInfo provided, pass through without interception
      if (!requestInfo) {
        return promise;
      }

      const apiKey = await generateApiKey(requestInfo);

      // Check if pre-fetched data exists for this API key
      if (preFetchedData && apiKey in preFetchedData) {
        return preFetchedData[apiKey] as T;
      }

      // No cached data - let API proceed and store result
      const data = await promise;
      if (conversationId && messageId && widgetId) {
        AnyaAPI.storeWidgetData({
          conversationId,
          messageId,
          widgetId,
          apiKey,
          data,
        });
      }
      return data;
    },
    [preFetchedData, conversationId, messageId, widgetId],
  );

  const contextValue = useMemo(() => ({ intercept }), [intercept]);

  return (
    <WidgetApiInterceptorContext.Provider value={contextValue}>
      {children}
    </WidgetApiInterceptorContext.Provider>
  );
}

export function useWidgetApiInterceptor() {
  return useContext(WidgetApiInterceptorContext);
}

export default WidgetApiInterceptorContext;
